import type { Outputs } from "@postdaily/api/routers/index";
import type { PlanCode } from "@postdaily/api/schemas/billing";
import {
  type BillingInterval,
  classifyPlanChange,
  type PlanChangeKind,
} from "@postdaily/api/services/plan-change-rules";

type BillingState = Outputs["billing"]["state"];

/**
 * The subscription a plan card is compared against — `null` for a workspace
 * with nothing paid, which picks plans through checkout as before.
 */
export type PlanSubscriber = {
  interval: BillingInterval | null;
  /**
   * The workspace holding the subscription when it isn't this one: plans can
   * only be changed there, so the cards here are read-only.
   */
  managedIn: string | null;
  pendingProductId: string | null;
  planCode: PlanCode;
  /** Null for a row not synced since the product was first stored. */
  productId: string | null;
};

export function subscriberFromState(
  state: BillingState | null | undefined,
): PlanSubscriber | null {
  if (!state || state.effectivePlanCode === "free") {
    return null;
  }

  const inherited = state.planSource === "owner";

  return {
    interval: state.billingInterval,
    managedIn: inherited ? state.billingWorkspaceName : null,
    pendingProductId: state.pendingChange?.productId ?? null,
    // The plan this workspace's OWN subscription is on — what a change moves
    // away from — even when a better plan elsewhere is what gets enforced.
    planCode: inherited ? state.effectivePlanCode : state.planCode,
    productId: state.productId,
  };
}

export type PlanCardAction =
  | { kind: "change"; label: string; changeKind: PlanChangeKind }
  | { kind: "checkout"; label: string }
  | { kind: "current"; label: string }
  | { kind: "manage"; label: string }
  | { kind: "unavailable"; label: string };

const changeLabels: Record<PlanChangeKind, string> = {
  downgrade: "Downgrade",
  switch_to_annual: "Switch to annual",
  switch_to_monthly: "Switch to monthly",
  upgrade: "Upgrade",
  upgrade_at_renewal: "Upgrade at renewal",
};

/**
 * What a plan card's button says and does. A subscriber sees the change a
 * click would make — worked out by the same rules the server applies, so the
 * label and the change can't disagree — and never a free-trial offer, which
 * only a brand-new subscription gets.
 */
export function planCardAction({
  interval,
  planGroup,
  productId,
  subscriber,
  trialDays,
}: {
  interval: BillingInterval;
  planGroup: PlanCode;
  productId: string | null;
  subscriber: PlanSubscriber | null;
  trialDays: number;
}): PlanCardAction {
  if (!subscriber) {
    if (planGroup === "free") {
      return { kind: "checkout", label: "Start free" };
    }

    return {
      kind: "checkout",
      label: trialDays > 0 ? `Start ${trialDays}-day free trial` : "Subscribe",
    };
  }

  const currentInterval = subscriber.interval ?? "month";
  const isCurrent = subscriber.productId
    ? productId === subscriber.productId
    : planGroup === subscriber.planCode && interval === currentInterval;

  if (isCurrent) {
    return { kind: "current", label: "Current plan" };
  }

  // Free has no product to move onto: leaving a paid plan is a cancellation,
  // which lives with the subscription in billing.
  if (planGroup === "free") {
    return { kind: "manage", label: "Manage in billing" };
  }

  if (subscriber.managedIn) {
    return { kind: "unavailable", label: `Managed in ${subscriber.managedIn}` };
  }

  if (productId !== null && productId === subscriber.pendingProductId) {
    return { kind: "unavailable", label: "Scheduled" };
  }

  const change = classifyPlanChange(
    { interval: currentInterval, planCode: subscriber.planCode },
    { interval, planCode: planGroup },
  );

  if (change.kind === "same") {
    return { kind: "current", label: "Current plan" };
  }

  return {
    changeKind: change.kind,
    kind: "change",
    label: changeLabels[change.kind],
  };
}
